import { FractionalRoundingLadder } from '../../src/constants/bcms';

/**
 * Converts decimal odds to fractional odds using a predefined rounding ladder.
 *
 * @param {number} decimalOdds - The decimal odds to convert.
 * @returns {string} Fractional odds in the format `numerator/denominator`, or `-` if input is invalid.
 *
 * The function works by:
 * - Returning `-` for invalid odds (decimal odds <= 1).
 * - Rounding the decimal odds to 4 decimal places for comparison.
 * - Iterating through the FractionalRoundingLadder to find:
 *    - An exact match (where the implied decimal equals the rounded input).
 *    - If no exact match, the closest fraction by smallest difference.
 * - Returns the best matching fraction as a string.
 *
 * @example
 * // Exact match in FractionalRoundingLadder
 * fractionalOdds(2.5); // returns "3/2"
 *
 * @example
 * // Closest match (no exact match in ladder)
 * fractionalOdds(2.48); // returns "6/5"
 */
const fractionalOdds = decimalOdds => {
  if (decimalOdds <= 1) return '-';
  // Round input for consistent comparison
  const roundedDecimal = Number(parseFloat(decimalOdds).toFixed(4));
  let exactMatch = null;
  let closestFraction = null;
  let smallestDiff = Infinity;

  // Iterate through possible fractions
  FractionalRoundingLadder.forEach(([num, denom]) => {
    const impliedDecimal = Number(parseFloat(((num / denom) + 1).toFixed(4))); // Calculate implied decimal from fraction

    // Check for exact match
    if (impliedDecimal === roundedDecimal) {
      exactMatch = [num, denom];
      return;
    }

    // Track the closest fraction if no exact match
    const diff = Math.abs(impliedDecimal - roundedDecimal);
    if (diff < smallestDiff) {
      smallestDiff = diff;
      closestFraction = [num, denom];
    }
  });

  // Use exact match if found, otherwise closest fraction
  const [n, d] = exactMatch || closestFraction;
  return `${n}/${d}`;
};

export default fractionalOdds;
